import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

// Import Spanish translations
import esCommon from './locales/es/common.json';
import esNavigation from './locales/es/navigation.json';
import esAuth from './locales/es/auth.json';
import esDashboard from './locales/es/dashboard.json';
import esAssets from './locales/es/assets.json';
import esRequisitions from './locales/es/requisitions.json';
import esDamage from './locales/es/damage.json';
import esRecovery from './locales/es/recovery.json';
import esRepairs from './locales/es/repairs.json';
import esInventory from './locales/es/inventory.json';
import esIncidents from './locales/es/incidents.json';
import esApprovals from './locales/es/approvals.json';
import esCatalogs from './locales/es/catalogs.json';
import esReports from './locales/es/reports.json';
import esEpcs from './locales/es/epcs.json';
import esClientsHub from './locales/es/clientsHub.json';
import esMaintenance from './locales/es/maintenance.json';
import esMetadata from './locales/es/metadata.json';
import esProductionOperations from './locales/es/productionOperations.json';
// Dynamic Flows namespaces (Phases 2-8.5)
import esWorkflows from './locales/es/workflows.json';
import esShipments from './locales/es/shipments.json';
import esOrders from './locales/es/orders.json';
import esReportsHub from './locales/es/reportsHub.json';
import esBilling from './locales/es/billing.json';
import esPalletManufacture from './locales/es/palletManufacture.json';
import esPalletIncidents from './locales/es/palletIncidents.json';
import esDeviceManagement from './locales/es/deviceManagement.json';
import esAi from './locales/es/ai.json';

// Import English translations
import enCommon from './locales/en/common.json';
import enNavigation from './locales/en/navigation.json';
import enAuth from './locales/en/auth.json';
import enDashboard from './locales/en/dashboard.json';
import enAssets from './locales/en/assets.json';
import enRequisitions from './locales/en/requisitions.json';
import enDamage from './locales/en/damage.json';
import enRecovery from './locales/en/recovery.json';
import enRepairs from './locales/en/repairs.json';
import enInventory from './locales/en/inventory.json';
import enIncidents from './locales/en/incidents.json';
import enApprovals from './locales/en/approvals.json';
import enCatalogs from './locales/en/catalogs.json';
import enReports from './locales/en/reports.json';
import enEpcs from './locales/en/epcs.json';
import enClientsHub from './locales/en/clientsHub.json';
import enMaintenance from './locales/en/maintenance.json';
import enMetadata from './locales/en/metadata.json';
import enProductionOperations from './locales/en/productionOperations.json';
// Dynamic Flows namespaces (Phases 2-8.5)
import enWorkflows from './locales/en/workflows.json';
import enShipments from './locales/en/shipments.json';
import enOrders from './locales/en/orders.json';
import enReportsHub from './locales/en/reportsHub.json';
import enBilling from './locales/en/billing.json';
import enPalletManufacture from './locales/en/palletManufacture.json';
import enPalletIncidents from './locales/en/palletIncidents.json';
import enDeviceManagement from './locales/en/deviceManagement.json';
import enAi from './locales/en/ai.json';

// Import tenant overrides
import tocaEsOverrides from './locales/overrides/toca/es.json';
import tocaEnOverrides from './locales/overrides/toca/en.json';
import sgsEsOverrides from './locales/overrides/sgs/es.json';
import sgsEnOverrides from './locales/overrides/sgs/en.json';

// Deep merge utility
function deepMerge<T extends Record<string, unknown>>(target: T, source: Partial<T>): T {
  const output = { ...target };

  for (const key in source) {
    if (Object.prototype.hasOwnProperty.call(source, key)) {
      const sourceValue = source[key];
      const targetValue = target[key];

      if (
        sourceValue &&
        typeof sourceValue === 'object' &&
        !Array.isArray(sourceValue) &&
        targetValue &&
        typeof targetValue === 'object' &&
        !Array.isArray(targetValue)
      ) {
        output[key] = deepMerge(
          targetValue as Record<string, unknown>,
          sourceValue as Record<string, unknown>
        ) as T[Extract<keyof T, string>];
      } else if (sourceValue !== undefined) {
        output[key] = sourceValue as T[Extract<keyof T, string>];
      }
    }
  }

  return output;
}

// Get tenant code from environment
const tenantCode = (import.meta.env.VITE_TENANT_CODE || 'TOCA').toUpperCase();

// Startup language resolution order:
//   1. the user's last explicit choice (localStorage), set by LanguageToggle
//   2. the build/tenant default (VITE_DEFAULT_LANGUAGE)
//   3. 'es' (also the fallbackLng below)
// AuthContext may later call i18n.changeLanguage() to apply the per-user
// preference returned by /auth/me once the user is known.
const buildDefaultLanguage = import.meta.env.VITE_DEFAULT_LANGUAGE || 'es';
const storedLanguage =
  typeof window !== 'undefined' ? window.localStorage.getItem('activeid-language') : null;
const defaultLanguage = storedLanguage || buildDefaultLanguage;

// Tenant overrides mapping
const tenantOverrides: Record<string, Record<string, Record<string, unknown>>> = {
  TOCA: {
    es: tocaEsOverrides,
    en: tocaEnOverrides,
  },
  SGS: {
    es: sgsEsOverrides,
    en: sgsEnOverrides,
  },
  DEMO: {
    es: sgsEsOverrides,
    en: sgsEnOverrides,
  },
};

// Get overrides for current tenant
const currentOverrides = tenantOverrides[tenantCode] || tenantOverrides.SGS;

// Build resources with tenant overrides merged in
const resources = {
  es: {
    common: deepMerge(esCommon, (currentOverrides.es?.common || {}) as typeof esCommon),
    navigation: deepMerge(esNavigation, (currentOverrides.es?.navigation || {}) as typeof esNavigation),
    auth: deepMerge(esAuth, (currentOverrides.es?.auth || {}) as typeof esAuth),
    dashboard: deepMerge(esDashboard, (currentOverrides.es?.dashboard || {}) as typeof esDashboard),
    assets: deepMerge(esAssets, (currentOverrides.es?.assets || {}) as typeof esAssets),
    requisitions: deepMerge(esRequisitions, (currentOverrides.es?.requisitions || {}) as typeof esRequisitions),
    damage: deepMerge(esDamage, (currentOverrides.es?.damage || {}) as typeof esDamage),
    recovery: deepMerge(esRecovery, (currentOverrides.es?.recovery || {}) as typeof esRecovery),
    repairs: deepMerge(esRepairs, (currentOverrides.es?.repairs || {}) as typeof esRepairs),
    inventory: deepMerge(esInventory, (currentOverrides.es?.inventory || {}) as typeof esInventory),
    incidents: deepMerge(esIncidents, (currentOverrides.es?.incidents || {}) as typeof esIncidents),
    approvals: deepMerge(esApprovals, (currentOverrides.es?.approvals || {}) as typeof esApprovals),
    catalogs: deepMerge(esCatalogs, (currentOverrides.es?.catalogs || {}) as typeof esCatalogs),
    reports: deepMerge(esReports, (currentOverrides.es?.reports || {}) as typeof esReports),
    epcs: deepMerge(esEpcs, (currentOverrides.es?.epcs || {}) as typeof esEpcs),
    clientsHub: deepMerge(esClientsHub, (currentOverrides.es?.clientsHub || {}) as typeof esClientsHub),
    maintenance: deepMerge(esMaintenance, (currentOverrides.es?.maintenance || {}) as typeof esMaintenance),
    metadata: deepMerge(esMetadata, (currentOverrides.es?.metadata || {}) as typeof esMetadata),
    productionOperations: deepMerge(esProductionOperations, (currentOverrides.es?.productionOperations || {}) as typeof esProductionOperations),
    workflows: deepMerge(esWorkflows, (currentOverrides.es?.workflows || {}) as typeof esWorkflows),
    shipments: deepMerge(esShipments, (currentOverrides.es?.shipments || {}) as typeof esShipments),
    orders: deepMerge(esOrders, (currentOverrides.es?.orders || {}) as typeof esOrders),
    reportsHub: deepMerge(esReportsHub, (currentOverrides.es?.reportsHub || {}) as typeof esReportsHub),
    billing: deepMerge(esBilling, (currentOverrides.es?.billing || {}) as typeof esBilling),
    palletManufacture: deepMerge(esPalletManufacture, (currentOverrides.es?.palletManufacture || {}) as typeof esPalletManufacture),
    palletIncidents: deepMerge(esPalletIncidents, (currentOverrides.es?.palletIncidents || {}) as typeof esPalletIncidents),
    deviceManagement: deepMerge(esDeviceManagement, (currentOverrides.es?.deviceManagement || {}) as typeof esDeviceManagement),
    ai: deepMerge(esAi, (currentOverrides.es?.ai || {}) as typeof esAi),
  },
  en: {
    common: deepMerge(enCommon, (currentOverrides.en?.common || {}) as typeof enCommon),
    navigation: deepMerge(enNavigation, (currentOverrides.en?.navigation || {}) as typeof enNavigation),
    auth: deepMerge(enAuth, (currentOverrides.en?.auth || {}) as typeof enAuth),
    dashboard: deepMerge(enDashboard, (currentOverrides.en?.dashboard || {}) as typeof enDashboard),
    assets: deepMerge(enAssets, (currentOverrides.en?.assets || {}) as typeof enAssets),
    requisitions: deepMerge(enRequisitions, (currentOverrides.en?.requisitions || {}) as typeof enRequisitions),
    damage: deepMerge(enDamage, (currentOverrides.en?.damage || {}) as typeof enDamage),
    recovery: deepMerge(enRecovery, (currentOverrides.en?.recovery || {}) as typeof enRecovery),
    repairs: deepMerge(enRepairs, (currentOverrides.en?.repairs || {}) as typeof enRepairs),
    inventory: deepMerge(enInventory, (currentOverrides.en?.inventory || {}) as typeof enInventory),
    incidents: deepMerge(enIncidents, (currentOverrides.en?.incidents || {}) as typeof enIncidents),
    approvals: deepMerge(enApprovals, (currentOverrides.en?.approvals || {}) as typeof enApprovals),
    catalogs: deepMerge(enCatalogs, (currentOverrides.en?.catalogs || {}) as typeof enCatalogs),
    reports: deepMerge(enReports, (currentOverrides.en?.reports || {}) as typeof enReports),
    epcs: deepMerge(enEpcs, (currentOverrides.en?.epcs || {}) as typeof enEpcs),
    clientsHub: deepMerge(enClientsHub, (currentOverrides.en?.clientsHub || {}) as typeof enClientsHub),
    maintenance: deepMerge(enMaintenance, (currentOverrides.en?.maintenance || {}) as typeof enMaintenance),
    metadata: deepMerge(enMetadata, (currentOverrides.en?.metadata || {}) as typeof enMetadata),
    productionOperations: deepMerge(enProductionOperations, (currentOverrides.en?.productionOperations || {}) as typeof enProductionOperations),
    workflows: deepMerge(enWorkflows, (currentOverrides.en?.workflows || {}) as typeof enWorkflows),
    shipments: deepMerge(enShipments, (currentOverrides.en?.shipments || {}) as typeof enShipments),
    orders: deepMerge(enOrders, (currentOverrides.en?.orders || {}) as typeof enOrders),
    reportsHub: deepMerge(enReportsHub, (currentOverrides.en?.reportsHub || {}) as typeof enReportsHub),
    billing: deepMerge(enBilling, (currentOverrides.en?.billing || {}) as typeof enBilling),
    palletManufacture: deepMerge(enPalletManufacture, (currentOverrides.en?.palletManufacture || {}) as typeof enPalletManufacture),
    palletIncidents: deepMerge(enPalletIncidents, (currentOverrides.en?.palletIncidents || {}) as typeof enPalletIncidents),
    deviceManagement: deepMerge(enDeviceManagement, (currentOverrides.en?.deviceManagement || {}) as typeof enDeviceManagement),
    ai: deepMerge(enAi, (currentOverrides.en?.ai || {}) as typeof enAi),
  },
};

i18n.use(initReactI18next).init({
  resources,
  lng: defaultLanguage,
  fallbackLng: 'es',
  defaultNS: 'common',
  ns: [
    'common',
    'navigation',
    'auth',
    'dashboard',
    'assets',
    'requisitions',
    'damage',
    'recovery',
    'repairs',
    'inventory',
    'incidents',
    'approvals',
    'catalogs',
    'reports',
    'epcs',
    'clientsHub',
    'maintenance',
    'metadata',
    'productionOperations',
    'workflows',
    'shipments',
    'reportsHub',
    'billing',
    'palletManufacture',
    'palletIncidents',
    'deviceManagement',
    'ai',
  ],
  interpolation: {
    escapeValue: false,
  },
  react: {
    useSuspense: false,
  },
});

export default i18n;
